import React from "react";
import { merge, $, media } from "glamor";
import { theme } from "styles/theme";
import { IDisplayFields } from "helpers/product-collection";
import { List, Record } from "immutable";
import { BrandField } from "./brand-field";
import { Item } from "constants/product-collection";
import ImageIcon from "components/icons/image";
import { getBrandNameForItem } from "helpers/collection";

export interface IItemReferenceCardProps {
    getSelectedImageUrl: () => string;
    detailFields: IDisplayFields;
    item: Record<any, any>;
    brandEntities: any;
    itemDisplayProperty?: List<any>;
}

export const IMAGE_HEIGHT = 96;

const ItemReferenceCard: React.FC<IItemReferenceCardProps> = ({
    getSelectedImageUrl,
    detailFields,
    item,
    brandEntities,
    itemDisplayProperty,
}) => {
    const selectedImageUrl = getSelectedImageUrl();
    const styles = React.useMemo(
        () =>
            merge(
                {
                    display: "flex",
                    overflow: "hidden",
                },
                $(" .image", {
                    flexShrink: 0,
                    flexGrow: 0,
                    height: "96px",
                    width: "96px",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    justifyContent: "center",
                    display: "flex",
                }),
                $(" .summary", {
                    height: "96px",
                    padding: "12px",
                    flexGrow: 1,
                    flexShrink: 1,
                }),
                $(" .styleNumber", {
                    color: theme.palette.darkGrey,
                    fontSize: "14px",
                    lineHeight: "16px",
                    paddingBottom: "16px",
                }),
                $(" h3", {
                    margin: "0 0 2px",
                    color: theme.palette.darkestGrey,
                    textTransform: "uppercase",
                    padding: "5px 0",
                }),
                $(" .brandName", {
                    color: `${theme.palette.secondaryTextColor} !important`,
                    padding: "0 !important",
                }),
                media(
                    "(max-width: 768px)",
                    $(" .summary", {
                        display: "none",
                    })
                )
            ),
        []
    );

    const { productName, styleNumber, brandId, brandName } = React.useMemo(() => {
        if (!item) {
            return {};
        }

        const { is_title, is_subtitle } = detailFields;
        return {
            productName: is_title && item.getIn(is_title.path),
            styleNumber: is_subtitle && item.getIn(is_subtitle.path),
            brandId: item.getIn(Item.FIELDS.BRAND_ID),
            brandName: getBrandNameForItem(itemDisplayProperty, item),
        };
    }, [item, detailFields, itemDisplayProperty]);

    const imageIconStyle = React.useMemo(() => ({ width: "80px", alignSelf: "center" }), []);
    return (
        <div {...styles}>
            {selectedImageUrl && (
                <div
                    className="image"
                    style={{
                        backgroundImage: `url(${selectedImageUrl}${
                            selectedImageUrl.includes("?") ? "&" : "?"
                        }max_height=${IMAGE_HEIGHT})`,
                    }}
                />
            )}
            {!selectedImageUrl && (
                <div className="image">
                    <ImageIcon style={imageIconStyle} />
                </div>
            )}
            <div className="summary">
                <BrandField
                    brandEntities={brandEntities}
                    brandId={brandId}
                    showBrandIcon={false}
                    brandNameOverride={!brandEntities?.[brandId] ? brandName : ""}
                />
                <h3>{productName}</h3>
                <div className="styleNumber">{styleNumber}</div>
            </div>
        </div>
    );
};

export { ItemReferenceCard };
